// Oude matras retour — adds an extra retour line item to the cart when the toggle is on.
// Strategy mirrors assets/montage-service.js: leave the theme's product-form flow intact
// (it adds the main product + our hidden pair-id property), then on a successful CartAddEvent
// add the chosen retour variant (eenpersoons/tweepersoons) in a separate /cart/add.js call.
import { CartAddEvent, ThemeEvents } from "@theme/events";

const DELIVERY_OVERRIDE_VALUE = "5-15 werkdagen";

class OudeMatrasRetourComponent extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.sizeInputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    this.form = this.findProductForm();

    if (!this.checkbox) return;

    this._onChange = this.onChange.bind(this);
    this.checkbox.addEventListener("change", this._onChange);

    this.syncCheckedClass();
    this.syncFormProperty();
    queueMicrotask(() => this.syncDeliveryOverride());

    this._onCartAdd = this.onCartAdd.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
  }

  disconnectedCallback() {
    if (this.checkbox && this._onChange) {
      this.checkbox.removeEventListener("change", this._onChange);
    }
    if (this._onCartAdd) {
      document.removeEventListener(ThemeEvents.cartUpdate, this._onCartAdd);
    }
  }

  findProductForm() {
    const ancestor = this.closest('form[data-type="add-to-cart-form"]');
    if (ancestor) return ancestor;
    const sectionRoot = this.closest("product-information-component, [data-section-id]");
    if (sectionRoot) {
      const formInSection = sectionRoot.querySelector('form[data-type="add-to-cart-form"]');
      if (formInSection) return formInSection;
    }
    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  onChange() {
    this.syncCheckedClass();
    this.syncFormProperty();
    this.syncDeliveryOverride();
  }

  syncCheckedClass() {
    if (!this.checkbox) return;
    this.classList.toggle("is-checked", this.checkbox.checked);
  }

  // The currently chosen retour variant id: checked size radio, else the data default.
  selectedVariantId() {
    const checked = this.sizeInputs.find((input) => input.checked);
    if (checked && checked.value) return checked.value;
    return this.dataset.defaultVariantId || "";
  }

  generatePairId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  ensureHiddenInput(name, value, dataKey) {
    const existing = this.form.querySelector(`input[${dataKey}="true"]`);
    if (existing) {
      existing.name = name;
      existing.value = value;
      return existing;
    }
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    input.setAttribute(dataKey, "true");
    this.form.appendChild(input);
    return input;
  }

  removeHiddenInput(dataKey) {
    const existing = this.form && this.form.querySelector(`input[${dataKey}="true"]`);
    if (existing) existing.remove();
  }

  // Add or remove a single hidden coupling property on the product form so it travels
  // along with the normal /cart/add submit. Underscore-prefix hides it in the cart UI.
  // The separate retour line item already shows the chosen size + price, so we deliberately
  // do NOT add a visible property (avoids a confusing duplicate).
  syncFormProperty() {
    if (!this.form || !this.checkbox) return;

    if (this.checkbox.checked) {
      if (!this._pairId) this._pairId = this.generatePairId();
      this.ensureHiddenInput("properties[_retour_pair_id]", this._pairId, "data-retour-pair-id");
    } else {
      this.removeHiddenInput("data-retour-pair-id");
      this._pairId = null;
    }
  }

  syncDeliveryOverride() {
    if (!this.checkbox) return;

    this.dispatchEvent(
      new CustomEvent("product-delivery-override", {
        bubbles: true,
        detail: {
          active: this.checkbox.checked,
          value: DELIVERY_OVERRIDE_VALUE,
        },
      })
    );
  }

  async onCartAdd(event) {
    if (!this.checkbox || !this.checkbox.checked) return;

    const source = event.detail && event.detail.data && event.detail.data.source;

    // Ignore our own dispatched events to avoid loops.
    if (source === "oude-matras-retour") return;

    // Only react to a successful add originating from the product form on this page.
    if (source !== "product-form-component") return;

    // Guard against double-firing for the same add.
    if (this._adding) return;
    this._adding = true;

    const retourVariantId = this.selectedVariantId();
    if (!retourVariantId) {
      this._adding = false;
      return;
    }

    try {
      const cartAddUrl =
        (window.MorgennTheme && window.MorgennTheme.routes && window.MorgennTheme.routes.cart_add_url) || "/cart/add.js";
      const cartUrl = (window.MorgennTheme && window.MorgennTheme.routes && window.MorgennTheme.routes.cart) || "/cart.js";

      const sectionIds = new Set();
      document.querySelectorAll("cart-items-component[data-section-id]").forEach((el) => {
        if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
      });

      const fd = new FormData();
      fd.append("id", retourVariantId);
      fd.append("quantity", "1");
      if (sectionIds.size > 0) {
        fd.append("sections", Array.from(sectionIds).join(","));
      }

      // Link this retour line item to its parent product via a shared pair-id.
      // The parent product was just submitted with the same _retour_pair_id property.
      const pairInput = this.form && this.form.querySelector('input[data-retour-pair-id="true"]');
      const pairId = this._pairId || (pairInput && pairInput.value);
      if (pairId) {
        fd.append("properties[_retour_pair_id]", pairId);
        fd.append("properties[_is_retour_addon]", "1");
      }

      const addRes = await fetch(cartAddUrl, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });

      if (!addRes.ok) {
        throw new Error("cart/add failed: " + addRes.status);
      }

      const addData = await addRes.json();

      const cartRes = await fetch(cartUrl);
      const cart = cartRes.ok ? await cartRes.json() : {};

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || "oude-matras-retour", {
          itemCount: cart.item_count,
          source: "oude-matras-retour",
          productId: retourVariantId,
          variantId: retourVariantId,
          sections: addData.sections,
        })
      );

      // Reset the toggle so the next product the customer adds doesn't auto-include retour.
      this.checkbox.checked = false;
      this.syncCheckedClass();
      this.syncFormProperty();
      this.syncDeliveryOverride();
    } catch (err) {
      console.error("[oude-matras-retour]", err);
    } finally {
      this._adding = false;
    }
  }
}

if (!customElements.get("oude-matras-retour-component")) {
  customElements.define("oude-matras-retour-component", OudeMatrasRetourComponent);
}
